import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LandingNav } from '../components/Nav';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useApp } from '../context/AppContext';
import { CHAPTERS } from '../data/chapters';
import { heroImageForChapter, heroImageAltForChapter } from '../data/chapterHeroImages';
import { DOCTORS } from '../data';
import { FooterNavLinks } from '../components/FooterNavLinks';
import ChapterLaunchBadge from '../components/ChapterLaunchBadge';
import NovaAvatarImg, { novaAvatarChapterFromSlug } from '../components/NovaAvatarImg';

const CHAPTER_USER_NAMES = {
  ttc: 'Jordan',
  fertility: 'Noelle',
  pregnancy: 'Ava',
  postpartum: 'Sofia',
};

function SectionLabel({ children, color = 'var(--text)' }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '.12em',
        textTransform: 'uppercase',
        color,
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
}

function EditorialSectionHeader({ label, title, em, sub, accent }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '.12em',
          textTransform: 'uppercase',
          color: 'var(--muted)',
          marginBottom: 10,
          textAlign: 'center',
        }}
      >
        {label}
      </div>
      <h2
        className="serif"
        style={{
          fontSize: 'clamp(24px, 4vw, 32px)',
          textAlign: 'center',
          color: 'var(--text)',
          marginBottom: 10,
          letterSpacing: '-.3px',
          lineHeight: 1.25,
        }}
      >
        {title}
        <br />
        <em style={{ fontStyle: 'italic', color: accent || 'var(--pink)' }}>{em}</em>
      </h2>
      {sub && (
        <p
          style={{
            fontSize: 14,
            color: 'var(--muted)',
            lineHeight: 1.75,
            margin: '0 auto',
            maxWidth: 620,
            textAlign: 'center',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {sub}
        </p>
      )}
    </div>
  );
}

function normalizeZip(value) {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.slice(0, 5);
}

function cleanHeroSubhead(text) {
  // Chapters' hero_desc includes a "HERO NOTE:" photography direction; strip it for display.
  return String(text || '').replace(/\s*HERO NOTE:[\s\S]*$/i, '').trim();
}

function zipToCity(zip) {
  // Minimal, deterministic ZIP -> city mapping (no external calls).
  // Expand this map if you add more doctor locations.
  const z = normalizeZip(zip);
  const prefix3 = z.slice(0, 3);
  const prefix2 = z.slice(0, 2);
  const map = {
    // Columbus, OH (examples)
    '430': 'Columbus, OH',
    '431': 'Columbus, OH',
    '432': 'Columbus, OH',
    '433': 'Columbus, OH',

    // Boston / Newton (legacy examples)
    '021': 'Boston, MA',
    '022': 'Boston, MA',
    '023': 'Boston, MA',
    '024': 'Newton, MA',
    '025': 'Newton, MA',
  };
  if (prefix3 && map[prefix3]) return map[prefix3];
  if (prefix2 && map[prefix2]) return map[prefix2];
  return '';
}

function parseDistanceMiles(distance) {
  const n = parseFloat(String(distance || '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : Infinity;
}

export default function ChapterPage() {
  const { chapter } = useParams();
  const navigate = useNavigate();
  const m = useMediaQuery('(max-width: 900px)');
  const { setNovaDraft, doctorReviews, getDoctorStats } = useApp();

  const page = useMemo(() => {
    return CHAPTERS.find((c) => c.id === chapter) || CHAPTERS[0];
  }, [chapter]);

  const [zip, setZip] = useState('');

  const accent = page.color;
  const heroSubhead = cleanHeroSubhead(page.hero_desc);

  const stageInfo = {
    ttc: { icon: '💜', name: 'Trying to Conceive', sub: '20s → 40s' },
    fertility: { icon: '🌸', name: 'Fertility', sub: '20s → 40s' },
    pregnancy: { icon: '🤰', name: 'Pregnancy', sub: 'Weeks 1 → Postpartum' },
    postpartum: { icon: '🤱', name: 'Motherhood', sub: 'Year 1 → Teen Years' },
  }[page.id] || { icon: '💜', name: page.name, sub: '' };

  const doctorCandidates = useMemo(() => {
    const chapterToDoctor = {
      ttc: 'ttc',
      fertility: 'ivf',
      pregnancy: 'pregnancy',
      postpartum: 'postpartum',
    };

    const target = chapterToDoctor[page.id] || '';
    const chapterFiltered = target
      ? DOCTORS.filter((d) => (d.chapters || []).includes(target) || d.bucket === target)
      : DOCTORS.slice();

    // If we don't yet have chapter-specific docs, fall back to all.
    const poolBase = chapterFiltered.length > 0 ? chapterFiltered : DOCTORS.slice();

    const city = zipToCity(zip);
    const poolLocated = city ? poolBase.filter((d) => (d.location || '').includes(city)) : poolBase;

    const pool = poolLocated.length > 0 ? poolLocated : poolBase;

    return pool
      .slice()
      .sort((a, b) => {
        const sa = getDoctorStats(a);
        const sb = getDoctorStats(b);
        return (
          (Number(sb.rating) - Number(sa.rating)) ||
          ((Number(sb.reviews) || 0) - (Number(sa.reviews) || 0)) ||
          (parseDistanceMiles(a.distance) - parseDistanceMiles(b.distance))
        );
      });
  }, [page.id, zip, doctorReviews, getDoctorStats]);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--sand)' }}>
      <div style={{ background: 'var(--white)', color: 'var(--text)' }}>
        <LandingNav />
      </div>

      {/* Hero: image LEFT, copy RIGHT (flopped from old layout) */}
      <header
        style={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: m ? '1fr' : '1fr 1fr',
          gridTemplateRows: m ? 'auto 1fr' : '1fr',
          minHeight: m ? 'auto' : 420,
          overflow: 'hidden',
        }}
      >
        {/* Image - left side on desktop, top on mobile */}
        <div
          style={{
            minHeight: m ? 240 : '100%',
            order: m ? 1 : 1,
          }}
        >
          <img
            src={heroImageForChapter(page.id)}
            alt={heroImageAltForChapter(page.id)}
            style={{
              width: '100%',
              height: '100%',
              minHeight: m ? 240 : 420,
              objectFit: 'cover',
              objectPosition: 'center',
            }}
          />
        </div>
        {/* Copy - right side on desktop, below image on mobile */}
        <div
          style={{
            background: accent,
            color: 'white',
            padding: m ? '36px 18px 32px' : '48px 56px 48px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            textAlign: 'left',
            order: m ? 2 : 2,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 10,
              marginBottom: 18,
              alignSelf: 'flex-start',
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 14px',
                borderRadius: 999,
                background: 'rgba(255,255,255,0.14)',
                border: '1px solid rgba(255,255,255,0.26)',
              }}
            >
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  background: 'rgba(255,255,255,0.18)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  flexShrink: 0,
                }}
              >
                {stageInfo.icon}
              </span>
              <div style={{ textAlign: 'left', lineHeight: 1.15 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: 'white' }}>{stageInfo.name}</div>
                {stageInfo.sub && (
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.86)', marginTop: 2 }}>{stageInfo.sub}</div>
                )}
              </div>
            </div>
            <ChapterLaunchBadge tone="hero">{page.launchBadge}</ChapterLaunchBadge>
          </div>

          <h1
            className="serif"
            style={{
              fontSize: m ? 'clamp(28px, 7vw, 40px)' : 48,
              lineHeight: 1.12,
              marginBottom: 14,
            }}
          >
            {page.tagline}
            {page.tagline_em ? (
              <>
                <br />
                <em style={{ fontStyle: 'italic' }}>{page.tagline_em}</em>
              </>
            ) : null}
          </h1>

          <p
            style={{
              fontSize: 15,
              color: 'rgba(255,255,255,0.92)',
              lineHeight: 1.7,
              marginBottom: 36,
            }}
          >
            {heroSubhead}
          </p>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignSelf: 'flex-start' }}>
            <button
              type="button"
              className="btn-primary"
              onClick={() => navigate(`/signup?source=chapter-${page.id}`)}
              style={{
                background: 'white',
                color: 'var(--text)',
                boxShadow: 'none',
                fontWeight: 700,
              }}
            >
              Join the waitlist
            </button>
            {!['ttc', 'pregnancy', 'postpartum'].includes(page.id) ? (
              <button
                type="button"
                onClick={() => navigate(`/demo/${page.id}/home`)}
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: 'white',
                  background: 'rgba(255,255,255,0.2)',
                  border: '2px solid rgba(255,255,255,0.6)',
                  padding: '12px 20px',
                  borderRadius: 999,
                  cursor: 'pointer',
                }}
              >
                See how it works
              </button>
            ) : null}
          </div>
        </div>
      </header>

      <main style={{ width: '100%', margin: 0, padding: 0 }}>
        {/* FEATURES */}
        <div style={{ background: 'var(--white)', paddingTop: m ? 48 : 64, paddingBottom: m ? 48 : 64 }}>
          <div style={{ maxWidth: 1040, margin: '0 auto', padding: m ? '0 18px' : '0 48px' }}>
            <EditorialSectionHeader
              label={page.id === 'cycle' ? 'Teen support' : 'Chapter features'}
              title={page.id === 'cycle' ? 'Support that meets you' : 'Six ways Nova shows up'}
              em={page.id === 'cycle' ? 'for your next step' : 'for your next step'}
              sub={
                page.id === 'cycle'
                  ? 'From period basics and comfort plans to scripts for talking to your mom and gentle mood support—everything you need to understand what you feel and ask for help.'
                  : 'Stage-matched support, built to help you ask better questions and move forward with confidence.'
              }
              accent={accent}
            />

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: m ? '1fr' : 'repeat(3, 1fr)',
                gap: 14,
              }}
            >
              {page.features.slice(0, 6).map((f) => (
                <div
                  key={f.title}
                  style={{
                    background: 'var(--white)',
                    border: '1px solid var(--border)',
                    borderRadius: 14,
                    padding: 18,
                    borderLeft: `3px solid ${accent}`,
                    boxSizing: 'border-box',
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      background: 'rgba(0,0,0,0.03)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 20,
                      marginBottom: 12,
                    }}
                  >
                    {f.icon}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)', marginBottom: 8 }}>{f.title}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.7 }}>{f.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ORA DEMO */}
        <div style={{ background: 'var(--sand)', paddingTop: m ? 48 : 64, paddingBottom: m ? 48 : 64, marginTop: 0 }}>
          <div style={{ maxWidth: 1040, margin: '0 auto', padding: m ? '0 18px' : '0 48px' }}>
            <EditorialSectionHeader
              label="Nova chat demo"
              title="Ask Nova what you need"
              em="and feel held"
              sub="Start with a suggestion chip. Nova carries your context forward so you don’t have to repeat yourself."
              accent={accent}
            />

            <div
              style={{
                background: 'var(--card)',
                border: '1px solid var(--border)',
                borderRadius: 18,
                padding: m ? 0 : 0,
                overflow: 'hidden',
              }}
            >
            {/*
              Chat preview: chapter hero image for the demo user; Nova uses the gradient N avatar.
            */}
            {/* Chat preview (visual like the homepage Nova section) */}
            <div
              style={{
                padding: '14px 16px',
                borderBottom: 'none',
                background: 'var(--white)',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <NovaAvatarImg size={36} alt="" chapter={novaAvatarChapterFromSlug(page.id)} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 900, color: 'var(--text)' }}>Nova — your OrivonAI companion</div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>{page.name} · available 24/7</div>
              </div>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: accent }} />
            </div>

            <div
              style={{
                padding: '14px 16px 12px',
                background: 'var(--sand)',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              {/* Assistant bubble */}
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <NovaAvatarImg size={26} alt="" chapter={novaAvatarChapterFromSlug(page.id)} />
                <div
                  style={{
                    background: 'var(--white)',
                    border: '1px solid var(--border)',
                    borderRadius: 14,
                    padding: '10px 12px',
                    fontSize: 12,
                    lineHeight: 1.65,
                    color: 'var(--text)',
                    maxWidth: '86%',
                    fontWeight: 500,
                  }}
                >
                  Hi. I’m Nova. What’s going on for you in <strong>{page.name}</strong> right now?
                </div>
              </div>

              {/* User bubble (use first suggestion as the example question) */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, alignItems: 'flex-start' }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 5,
                    marginTop: 2,
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      background: 'var(--pink)',
                    }}
                  >
                    <img
                      src={heroImageForChapter(page.id)}
                      alt={heroImageAltForChapter(page.id)}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--muted)', lineHeight: 1, textAlign: 'center' }}>
                    {CHAPTER_USER_NAMES[page.id] || 'You'}
                  </div>
                </div>
                <div
                  style={{
                    background: 'var(--pink-m)',
                    color: 'var(--pink-dk)',
                    borderRadius: 14,
                    padding: '10px 12px',
                    fontSize: 12,
                    lineHeight: 1.6,
                    maxWidth: '86%',
                    border: '1px solid rgba(212,83,126,0.18)',
                  }}
                >
                  {page.suggestions?.[0] || 'What should I do next?'}
                </div>
              </div>

              {/* Assistant follow-up */}
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <NovaAvatarImg size={26} alt="" chapter={novaAvatarChapterFromSlug(page.id)} />
                <div
                  style={{
                    background: 'var(--white)',
                    border: '1px solid var(--border)',
                    borderRadius: 14,
                    padding: '10px 12px',
                    fontSize: 12,
                    lineHeight: 1.6,
                    color: 'var(--text)',
                    maxWidth: '86%',
                  }}
                >
                  That makes sense. I’ll help you understand what’s normal and what to do next—step by step, in plain language.
                </div>
              </div>

              <div style={{ marginTop: 2 }}>
                <div style={{ fontSize: 11, fontWeight: 900, color: 'var(--pink-dk)', marginBottom: 8, letterSpacing: '.08em', textTransform: 'uppercase' }}>
                  Question ideas
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {page.suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => {
                        setNovaDraft(s);
                        navigate('/nova');
                      }}
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        padding: '7px 12px',
                        borderRadius: 999,
                        border: `1px solid ${accent}66`,
                        color: 'var(--text)',
                        background: 'rgba(255,255,255,0.65)',
                        cursor: 'pointer',
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Open in Nova CTA (homepage-like) */}
              <div
                style={{
                  marginTop: 14,
                  paddingTop: 12,
                  display: 'flex',
                  flexDirection: m ? 'column' : 'row',
                  gap: 10,
                  alignItems: 'stretch',
                }}
              >
                <div
                  style={{
                    flex: 1,
                    background: 'var(--white)',
                    border: '1px solid var(--border)',
                    borderRadius: 12,
                    padding: '10px 12px',
                    fontSize: 12,
                    color: 'var(--muted)',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Ask Nova anything about your journey…
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const seed = page.suggestions?.[0] || 'What should I ask Nova first?';
                    setNovaDraft(seed);
                    navigate('/nova');
                  }}
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: 'white',
                    background: 'var(--pink)',
                    border: 'none',
                    padding: '12px 14px',
                    borderRadius: 12,
                    cursor: 'pointer',
                    width: m ? '100%' : 'auto',
                    flexShrink: 0,
                    boxShadow: 'none',
                  }}
                >
                  Open In Nova
                </button>
              </div>
            </div>
            </div>
          </div>
        </div>

        {/* COMMUNITIES */}
        <div style={{ background: 'var(--white)', paddingTop: m ? 48 : 64, paddingBottom: m ? 48 : 64 }}>
          <div style={{ maxWidth: 1040, margin: '0 auto', padding: m ? '0 18px' : '0 48px' }}>
            <p
              style={{
                textAlign: 'center',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '.12em',
                textTransform: 'uppercase',
                color: 'var(--muted)',
                marginBottom: 10,
              }}
            >
              Community — matched across every journey
            </p>
            <h2
              className="serif"
              style={{
                fontFamily: 'Playfair Display,serif',
                fontSize: m ? 26 : 32,
                textAlign: 'center',
                color: 'var(--text)',
                marginBottom: 10,
                letterSpacing: '-.3px',
                lineHeight: 1.3,
              }}
            >
              Find your people,
              <br />
              <em style={{ fontStyle: 'italic', color: accent }}>wherever you are</em>
            </h2>
            <p style={{ textAlign: 'center', fontSize: 14, color: 'var(--muted)', maxWidth: 460, margin: '0 auto 36px', lineHeight: 1.7 }}>
              Every community in this chapter is stage-specific, moderated, and AI-recommended. You'll never feel like you're in the
              wrong room.
            </p>

            <div
              style={{
                background: 'var(--card)',
                border: '1px solid var(--border)',
                borderRadius: 14,
                overflow: 'hidden',
                maxWidth: 680,
                margin: '0 auto',
              }}
            >
              <div
                style={{
                  padding: '14px 18px',
                  borderBottom: `2.5px solid ${accent}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Recommended for you</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>Matched to your journey stage and needs</div>
                </div>
                <span
                  style={{ fontSize: 11, color: 'var(--pink)', fontWeight: 600, cursor: 'pointer' }}
                  onClick={() => navigate('/community')}
                >
                  See all
                </span>
              </div>

              {page.communities.slice(0, 5).map((c, idx) => {
                const pillBg = `${accent}22`;
                const members = Number.isFinite(c.members) ? c.members : null;
                return (
                  <div
                    key={c.title || idx}
                    style={{
                      padding: '13px 18px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      borderBottom: `2.5px solid ${accent}`,
                      flexWrap: m ? 'wrap' : 'nowrap',
                    }}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        background: `${accent}18`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 19,
                        flexShrink: 0,
                      }}
                    >
                      {c.emoji || '🌙'}
                    </div>

                    <div style={{ minWidth: m ? 'calc(100% - 52px)' : 0, flex: m ? '1 1 100%' : undefined }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{c.title}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{c.desc}</div>
                    </div>

                    <div
                      style={{
                        marginLeft: m ? 0 : 'auto',
                        width: m ? '100%' : 'auto',
                        textAlign: m ? 'left' : 'right',
                        flexShrink: 0,
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 8,
                        justifyContent: m ? 'flex-start' : 'flex-end',
                      }}
                    >
                      {members !== null && <div style={{ fontSize: 11, color: 'var(--muted)' }}>{members.toLocaleString()} members</div>}
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          padding: '3px 9px',
                          borderRadius: 999,
                          background: pillBg,
                          color: accent,
                        }}
                      >
                        {c.tag || page.name}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* DOCTORS */}
        <div style={{ background: 'var(--sand)', paddingTop: m ? 48 : 64, paddingBottom: m ? 48 : 64 }}>
          <div style={{ maxWidth: 1040, margin: '0 auto', padding: m ? '0 18px' : '0 48px' }}>
            <EditorialSectionHeader
              label="Doctors"
              title="Find clinical support"
              em="ready for your questions"
              sub="AI-matched guidance so you can walk into appointments with context and clarity."
              accent={accent}
            />
            <div
              style={{
                background: 'var(--card)',
                border: '1px solid var(--border)',
                borderRadius: 14,
                padding: '16px 18px',
                display: 'flex',
                gap: 10,
                flexWrap: 'wrap',
                alignItems: 'center',
                marginBottom: 14,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text)', letterSpacing: '.04em' }}>ZIP</div>
              <input
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                placeholder="Enter ZIP (e.g., 02115)"
                inputMode="numeric"
                style={{
                  flex: 1,
                  minWidth: 220,
                  fontSize: 13,
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  padding: '10px 12px',
                  background: 'var(--sand)',
                  color: 'var(--text)',
                  outline: 'none',
                  fontWeight: 500,
                }}
              />
              <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.4 }}>
                Showing top-rated doctors (Google-style rating). Ranking refines by ZIP → city match.
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: m ? '1fr' : 'repeat(3, 1fr)', gap: 14 }}>
              {doctorCandidates.map((d) => (
                <div
                  key={d.id}
                  style={{
                    background: 'var(--white)',
                    border: '1px solid var(--border)',
                    borderRadius: 14,
                    padding: 18,
                    borderLeft: `3px solid ${accent}`,
                    boxSizing: 'border-box',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--text)', marginBottom: 4 }}>{d.name}</div>
                      <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6, marginBottom: 8 }}>
                        {d.specialty} · {d.clinic}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 800,
                        padding: '3px 10px',
                        borderRadius: 999,
                        flexShrink: 0,
                        background: d.status === 'accepting' ? 'var(--sage-l)' : 'var(--amber-l)',
                        color: d.status === 'accepting' ? 'var(--sage-d)' : 'var(--amber)',
                        border: '1px solid var(--border)',
                        textAlign: 'center',
                      }}
                    >
                      {d.status === 'accepting' ? 'Accepting' : d.waitTime ? `Waitlist · ${d.waitTime}` : 'Waitlist'}
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
                    {(() => {
                      const stats = getDoctorStats(d);
                      return (
                        <>
                          <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--pink-dk)' }}>★ {stats.rating}</span>
                          <span style={{ fontSize: 12, color: 'var(--muted)' }}>{stats.reviews} reviews</span>
                        </>
                      );
                    })()}
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>📍 {d.location}</span>
                    {d.distance && (
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                        · {d.distance}
                      </span>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setNovaDraft(`Can you help me prepare for an appointment with ${d.name}?`);
                      navigate('/nova');
                    }}
                    style={{
                      width: '100%',
                      fontSize: 12,
                      fontWeight: 900,
                      color: 'white',
                      background: 'var(--pink)',
                      border: 'none',
                      padding: '10px 14px',
                      borderRadius: 999,
                      cursor: 'pointer',
                      boxShadow: 'none',
                    }}
                  >
                    Ask Nova about {d.name.split(' ')[1]}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* CTA band (accent) + separate footer links (white) */}
      <div>
        {/* Centered CTA band */}
        <footer style={{ background: accent, color: 'white', padding: m ? '26px 18px' : '36px 48px' }}>
          <div
            style={{
              maxWidth: 1040,
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 14,
              textAlign: 'center',
            }}
          >
            <div style={{ maxWidth: 640 }}>
              <div className="serif" style={{ fontSize: m ? 22 : 30, lineHeight: 1.2, fontWeight: 700 }}>
                Start with Nova in {page.name}
              </div>
              <div style={{ fontSize: 14, opacity: 0.95, marginTop: 6 }}>{page.nova_intro}</div>
            </div>

            <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn-primary"
                onClick={() => navigate('/signup')}
                style={{
                  background: 'white',
                  color: 'var(--text)',
                  boxShadow: 'none',
                }}
              >
                Start Free With Nova
              </button>
            </div>
          </div>
        </footer>

        {/* Footer links area */}
        <div style={{ background: 'var(--white)', borderTop: `2.5px solid ${accent}` }}>
          <div style={{ maxWidth: 1040, margin: '18px auto 0', padding: m ? '0 18px 24px' : '0 48px 28px' }}>
            <FooterNavLinks linkColor="var(--muted)" />
          </div>
        </div>
      </div>
    </div>
  );
}

